/* # Role and Permission Utility
   - Fixes role label translation by ensuring role keys match the i18n configuration
   - Implements case-insensitive role checks for better reliability 
*/

export const ROLES = {
  SUPERADMIN: 'SuperAdmin',
  ADMINISTRATOR: 'Administrator',
  MANAGER: 'BuildingManager',
  GENERAL_USER: 'BuildingUser'
};

export const ROLE_NAMES = {
  [ROLES.SUPERADMIN]: 'Superadmin',
  [ROLES.ADMINISTRATOR]: 'Administrator',
  [ROLES.MANAGER]: 'Manager',
  [ROLES.GENERAL_USER]: 'GeneralUser'
};

const normalizeRole = (role) => {
  if (!role) return '';
  let r = role;
  if (typeof role === 'object') {
    r = role.role_code || role.role || '';
  }
  return String(r).trim();
};

// Core Role Checks (Case Insensitive)
export const isSuperadmin = (role) => {
  const r = normalizeRole(role).toLowerCase();
  return r === 'superadmin';
};

export const isAdministrator = (role) => {
  const r = normalizeRole(role).toLowerCase();
  return r === 'administrator' || r === 'admin';
};

export const isManager = (role) => {
  const r = normalizeRole(role).toLowerCase();
  return r === 'buildingmanager' || r === 'manager';
};

export const isGeneralUser = (role) => {
  const r = normalizeRole(role).toLowerCase();
  return r === 'buildinguser' || r === 'generaluser' || r === 'general user';
};

// Management tier (SuperAdmin, Admin, BuildingManager)
export const isManagementTier = (role) => {
  return isSuperadmin(role) || isAdministrator(role) || isManager(role);
};

//============ Permission Checks ============

export const canViewInternalComments = (role) => isManagementTier(role);

export const canModifyFaultReport = (user, fault) => {
  if (!user || !fault) return false;
  // Management tier can modify anything
  if (isManagementTier(user.role)) return true;
  // Owners can modify their own reports
  const creatorId = fault.created_by || fault.reporter_id;
  return Number(user.id) === Number(creatorId);
};

export const canUpdateFaultStatus = (role) => isManagementTier(role);

export const canDeleteUser = (role, targetUserRole) => {
  if (isSuperadmin(role)) return true;
  if (isAdministrator(role) && !isSuperadmin(targetUserRole)) return true;
  return false;
};

// Property Access
export const hasAccessToAllProperties = (role) => isSuperadmin(role) || isAdministrator(role);
export const canAccessPropertySetup = (role) => isSuperadmin(role) || isAdministrator(role);
export const canAccessUserManagement = (role) => isSuperadmin(role) || isAdministrator(role);

// Feature Access
export const canViewAllFaultReports = (role) => isManagementTier(role);
export const canManageAnnouncements = (role) => isManagementTier(role);

// Settings - Allow ALL users to access settings for notification preferences
export const canAccessSettings = (role) => true;
export const canAccessFullSettings = (role) => isSuperadmin(role) || isAdministrator(role);

export const canAccessAnalytics = (role) => isSuperadmin(role) || isAdministrator(role);

// UI Helpers
export const getRoleLabel = (role) => {
  // Import translation at runtime if available
  const i18n = window.i18next;

  if (typeof role === 'object' && role.role_name) return role.role_name;

  const r = normalizeRole(role);
  const lowerR = r.toLowerCase();

  let roleKey = 'GeneralUser';

  if (lowerR === 'superadmin') {
    roleKey = 'Superadmin';
  } else if (lowerR === 'administrator' || lowerR === 'admin') {
    roleKey = 'Administrator';
  } else if (lowerR === 'buildingmanager' || lowerR === 'manager') {
    roleKey = 'Manager';
  } else if (lowerR === 'buildinguser' || lowerR === 'generaluser' || lowerR === 'general user') {
    roleKey = 'GeneralUser';
  } else {
    // Fallback to the code itself if it's unknown but mapped
    roleKey = ROLE_NAMES[r] || 'GeneralUser';
  }

  // Try to use translation if available
  if (i18n && i18n.t) {
    const translated = i18n.t(`roles.${roleKey}`);
    // If translation returns the key (meaning it failed), return a readable fallback
    if (translated === `roles.${roleKey}`) {
      const fallbacks = {
        'GeneralUser': 'General User',
        'Manager': 'Manager',
        'Superadmin': 'Superadmin',
        'Administrator': 'Administrator'
      };
      return fallbacks[roleKey] || roleKey;
    }
    return translated;
  }

  // Fallback for when i18n is not available
  const roleFallback = {
    'GeneralUser': 'General User',
    'Manager': 'Manager',
    'Superadmin': 'Superadmin',
    'Administrator': 'Administrator'
  };
  return roleFallback[roleKey] || roleKey;
};

export const getRoleBadgeColor = (role) => {
  const r = normalizeRole(role);
  if (isSuperadmin(r)) return 'bg-red-100 text-red-700 border-red-200';
  if (isAdministrator(r)) return 'bg-purple-100 text-purple-700 border-purple-200';
  if (isManager(r)) return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
};